import { Tooltip } from 'antd'
import {
  EditActionButton,
  EditActions,
  EditContainer,
  EditTextArea,
  MessageActionButton,
  openTooltipStyle,
  StopIconButton,
  TextMessage,
  UserMessageBubble,
  UserMessageContainer,
  UserMessageRow,
} from 'features/chatbot/components/conversationMessage/styles'
import { ChatbotDataContext } from 'features/chatbot/contexts/ChatbotDataLayer'
import { useChatbotFeedbackOptional } from 'features/chatbot/contexts/ChatbotFeedbackContext'
import { Check, Pencil, RotateCcw, Square, X } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'

interface UserMessageProps {
  text: string
  itemIndex: number
  isLastUserMessage: boolean
}

export const UserMessage: React.FC<UserMessageProps> = ({
  text,
  itemIndex,
  isLastUserMessage,
}) => {
  const {
    isThinking,
    cancelGeneration,
    editingMessageIndex,
    startEditMessage,
    cancelEditMessage,
    submitEditMessage,
    resendMessage,
  } = useContext(ChatbotDataContext)
  const feedbackContext = useChatbotFeedbackOptional()

  const [editText, setEditText] = useState(text)

  const isEditing = editingMessageIndex === itemIndex

  useEffect(() => {
    if (isEditing) {
      setEditText(text)
    }
  }, [isEditing, text])

  const handleEditClick = () => {
    startEditMessage(itemIndex)
  }

  const handleCancelEdit = () => {
    setEditText(text)
    cancelEditMessage()
  }

  const handleSubmitEdit = () => {
    if (editText.trim() && editText.trim() !== text) {
      feedbackContext?.recordEditMessage(itemIndex, text, editText.trim())
      submitEditMessage(editText.trim())
    } else {
      cancelEditMessage()
    }
  }

  const handleStopGeneration = () => {
    feedbackContext?.recordStopGeneration(itemIndex)
    cancelGeneration()
  }

  const handleResend = () => {
    feedbackContext?.recordResendMessage(itemIndex, text)
    resendMessage(text)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmitEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  if (isEditing) {
    return (
      <EditContainer>
        <EditTextArea
          autoFocus
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onKeyDown={handleKeyDown}
          autoSize={{ minRows: 1, maxRows: 6 }}
        />
        <EditActions>
          <EditActionButton onClick={handleCancelEdit}>
            <X size={10} />
            Cancel
          </EditActionButton>
          <EditActionButton
            $variant='primary'
            onClick={handleSubmitEdit}
            disabled={!editText.trim()}>
            <Check size={10} />
            Save & Resend
          </EditActionButton>
        </EditActions>
      </EditContainer>
    )
  }

  return (
    <UserMessageContainer>
      <UserMessageRow>
        {isLastUserMessage && !isThinking && (
          <>
            <Tooltip
              title='Resend'
              placement='top'
              color='#1e1e1e'
              arrow={false}
              styles={{ container: openTooltipStyle }}>
              <MessageActionButton onClick={handleResend}>
                <RotateCcw />
              </MessageActionButton>
            </Tooltip>
            <Tooltip
              title='Edit'
              placement='top'
              color='#1e1e1e'
              arrow={false}
              styles={{ container: openTooltipStyle }}>
              <MessageActionButton onClick={handleEditClick}>
                <Pencil />
              </MessageActionButton>
            </Tooltip>
          </>
        )}
        {isLastUserMessage && isThinking ? (
          <UserMessageBubble>
            {text}
            <Tooltip
              title='Stop'
              placement='top'
              color='#1e1e1e'
              arrow={false}
              styles={{ container: openTooltipStyle }}>
              <StopIconButton onClick={handleStopGeneration}>
                <Square />
              </StopIconButton>
            </Tooltip>
          </UserMessageBubble>
        ) : (
          <TextMessage $isUser={true}>{text}</TextMessage>
        )}
      </UserMessageRow>
    </UserMessageContainer>
  )
}
